import {Fragment, useEffect} from "react";
import {useParams} from "react-router-dom";
import {Job} from "../../types/Job";
import {DispatchOrder} from "../../types/DispatchOrder";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {DateUtility} from "../common/DateUtility";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {JobDetailDispatchOrderBox} from "./JobDetailDispatchOrderBox";
import {PhoneUtility} from "../common/PhoneUtility";
import {LocationUtility} from "../common/LocationUtility";

type JobDetailPageRouteParams = {
    jobId: string;
};

export let JobDetailPage = function () {
    const params = useParams<JobDetailPageRouteParams>();
    const dataStore = useRenderableDataStore<{ loading: boolean; job: Job | undefined }>({
        loading: true,
        job: undefined,
    });

    function refreshJob() {
        dataStore.updateAndRender({loading: true});
        ApiUtility.getJob("" + params.jobId)
            .then(function (job: Job) {
                dataStore.updateAndRender({job: job, loading: false});
            })
            .catch(function (error: any) {
                console.log(error);
                dataStore.updateAndRender({loading: false});
                MessageDisplayerUtility.error("An error occurred loading the job");
            });
    }

    useEffect(function () {
        refreshJob();
    }, [params.jobId]);

    function renderTimeline(dispatchOrders: DispatchOrder[]) {
        if (dispatchOrders.length === 0) {
            return (
                <div className="job-detail-empty-message">
                    No dispatch orders found
                </div>
            );
        }

        const ordersByDate: { [date: string]: DispatchOrder[] } = {};
        for (let i = 0; i < dispatchOrders.length; i++) {
            const order = dispatchOrders[i];
            if (!ordersByDate[order.requested_for_date]) {
                ordersByDate[order.requested_for_date] = [];
            }
            ordersByDate[order.requested_for_date].push(order);
        }
        const uniqueDatesDescending = Object.keys(ordersByDate).sort(function (dateA, dateB) {
            const timestampA = DateUtility.urlDateToMoment(dateA).unix();
            const timestampB = DateUtility.urlDateToMoment(dateB).unix();
            return timestampB - timestampA;
        });
        uniqueDatesDescending.forEach(function (date) {
            ordersByDate[date].sort(function (orderA, orderB) {
                return orderA.id - orderB.id;
            });
        });

        return uniqueDatesDescending.map(function (date, index) {
            const isLast = index === uniqueDatesDescending.length - 1;
            const ordersForDate = ordersByDate[date];
            const chipMoment = DateUtility.urlDateToMoment(date);
            const chipDayOfWeek = chipMoment.format("dd");
            const chipDayNumber = chipMoment.format("DD");
            const chipMonthAbbreviation = chipMoment.format("MMM").toUpperCase();
            return (
                <div className="job-detail-timeline-date-row" key={date}>
                    <div className="job-detail-date-chip-column">
                        <div className="job-detail-date-chip">
                            <div className="job-detail-date-chip-top">
                                <div className="job-detail-date-chip-dow">{chipDayOfWeek}</div>
                                <div className="job-detail-date-chip-day-number">{chipDayNumber}</div>
                            </div>
                            <div className="job-detail-date-chip-month">{chipMonthAbbreviation}</div>
                        </div>
                        {!isLast && <div className="job-detail-date-connector"/>}
                    </div>
                    <div className="job-detail-dispatch-orders-column">
                        {ordersForDate.map(function (dispatchOrder) {
                            return (
                                <JobDetailDispatchOrderBox
                                    key={dispatchOrder.id}
                                    dispatchOrder={dispatchOrder}
                                />
                            );
                        })}
                    </div>
                </div>
            );
        });
    }

    const loadedJob = dataStore.data().job;
    const rootClass = `job-detail-page${loadedJob && !loadedJob.active ? ' inactive' : ''}`;

    return (
        <div className={rootClass}>
            <ConditionalRenderer if={!!dataStore.data().job} renderer={() => {
                const job = dataStore.data().job as Job;
                const dispatchOrders = job.dispatch_orders || [];
                const showAutoSyncDisabledBanner = (job.subsequent_auto_sync_failures || 0) >= 3 && !!job.acumatica_id;
                return (
                    <Fragment>
                        {showAutoSyncDisabledBanner && (
                            <div className="job-detail-page-auto-sync-disabled-banner">
                                <div className="job-detail-page-auto-sync-disabled-banner-content">
                                    <img
                                        src="/static/images/warning-triangle-light-gray.svg"
                                        className="job-detail-page-auto-sync-disabled-banner-icon"
                                    />
                                    <div className="job-detail-page-auto-sync-disabled-banner-text">
                                        {'Automatic sync is disabled for this job due to repeated errors. Successfully manually syncing this job will re-enable auto sync.'}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="job-detail-page-content">
                            <div className="job-detail-info-column">
                                <div className="job-detail-job-number-pill">{job.job_number}</div>
                                <div className="job-detail-job-name">{job.display_name}</div>
                                <div className="job-detail-job-location">
                                    {LocationUtility.locationLabel(job.location)}
                                </div>
                                <div className="job-detail-job-foremen">
                                    {job.ordered_user_jobs && job.ordered_user_jobs.length > 0 ? (
                                        job.ordered_user_jobs.map((userJob) => (
                                            <div key={userJob.id} className="job-detail-job-foreman-row">
                                                <span
                                                    className="job-detail-job-foreman-name"
                                                >{userJob.user.person.name}</span>
                                                <span
                                                    className="job-detail-job-foreman-phone"
                                                >{PhoneUtility.dbPhoneToParenthesesDisplayPhone(userJob.user.person.phone)}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <span className="job-detail-job-foreman-empty">No foremen assigned</span>
                                    )}
                                </div>
                            </div>
                            <div className="job-detail-timeline-column">
                                {renderTimeline(dispatchOrders)}
                            </div>
                        </div>
                    </Fragment>
                );
            }}/>
        </div>
    );
};
